import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Landing Page with Auth
const Landing = ({ onLogin }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [role, setRole] = useState('client');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const endpoint = isLogin ? '/api/auth/login' : '/api/auth/register';
      const payload = isLogin 
        ? { email, password }
        : { email, password, name, phone, role, id_document_base64: '' };
      
      const res = await axios.post(`${BACKEND_URL}${endpoint}`, payload);
      alert(isLogin ? 'Login successful!' : 'Account created!');
      onLogin(res.data.token, res.data.user);
    } catch (error) {
      alert(error.response?.data?.detail || 'Authentication failed');
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl p-8 shadow-lg">
        <h1 className="text-3xl font-bold text-primary mb-2">The Cleaning Company</h1>
        <p className="text-stone-600 mb-6">Professional Cleaning Marketplace</p>
        
        <div className="mb-6">
          <button
            onClick={() => setIsLogin(true)}
            className={`px-6 py-2 rounded-full mr-2 ${isLogin ? 'bg-primary text-white' : 'bg-stone-200'}`}
          >
            Login
          </button>
          <button
            onClick={() => setIsLogin(false)}
            className={`px-6 py-2 rounded-full ${!isLogin ? 'bg-primary text-white' : 'bg-stone-200'}`}
          >
            Register
          </button>
        </div>

        {!isLogin && (
          <div className="mb-4 flex gap-2">
            {['client', 'provider', 'admin'].map(r => (
              <button
                key={r}
                onClick={() => setRole(r)}
                className={`flex-1 py-2 rounded-full capitalize ${role === r ? 'bg-primary text-white' : 'bg-stone-100'}`}
              >
                {r}
              </button>
            ))}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {!isLogin && (
            <input
              type="text"
              placeholder="Full Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full px-4 py-3 rounded-xl border border-stone-300 focus:outline-none focus:ring-2 focus:ring-primary"
            />
          )}
          
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full px-4 py-3 rounded-xl border border-stone-300 focus:outline-none focus:ring-2 focus:ring-primary"
          />
          
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="w-full px-4 py-3 rounded-xl border border-stone-300 focus:outline-none focus:ring-2 focus:ring-primary"
          />
          
          {!isLogin && (
            <input
              type="tel"
              placeholder="Phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
              className="w-full px-4 py-3 rounded-xl border border-stone-300 focus:outline-none focus:ring-2 focus:ring-primary"
            />
          )}
          
          <button
            type="submit"
            className="w-full bg-primary text-white py-3 rounded-full font-semibold hover:bg-opacity-90"
          >
            {isLogin ? 'Sign In' : 'Create Account'}
          </button>
        </form>
      </div>
    </div>
  );
};

// Client Dashboard
const ClientDashboard = ({ user, onLogout }) => {
  const [jobs, setJobs] = useState([]);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/jobs`);
      setJobs(res.data);
    } catch (error) {
      console.error('Failed to fetch jobs', error);
    }
  };

  if (showCreate) {
    return <CreateJob user={user} onBack={() => { setShowCreate(false); fetchJobs(); }} />;
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <header className="bg-white border-b border-stone-200 p-4">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-primary">Client Dashboard</h1>
            <p className="text-stone-600">Welcome, {user.name}</p>
          </div>
          <button onClick={onLogout} className="px-6 py-2 rounded-full bg-stone-200 hover:bg-stone-300">
            Logout
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-4">
        <button
          onClick={() => setShowCreate(true)}
          className="mb-6 px-8 py-3 bg-primary text-white rounded-full font-semibold hover:bg-opacity-90"
        >
          + Create New Job
        </button>

        <h2 className="text-xl font-bold mb-4">Your Jobs</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {jobs.map(job => (
            <div key={job.id} className="bg-white p-6 rounded-2xl shadow">
              <div className="flex justify-between items-start mb-4">
                <span className={`px-3 py-1 rounded-full text-sm ${
                  job.status === 'completed' ? 'bg-green-100 text-green-800' :
                  job.status === 'accepted' ? 'bg-blue-100 text-blue-800' :
                  'bg-yellow-100 text-yellow-800'
                }`}>
                  {job.status}
                </span>
                <span className="text-2xl font-bold text-primary">${job.pricing?.total}</span>
              </div>
              <h3 className="font-semibold text-lg capitalize mb-2">{job.service_type?.replace('_', ' ')}</h3>
              <p className="text-stone-600 text-sm">{job.address}</p>
              <p className="text-stone-500 text-xs mt-2">Provider: {job.provider_name || 'Pending'}</p>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
};

// Create Job Component
const CreateJob = ({ user, onBack }) => {
  const [serviceType, setServiceType] = useState('regular_cleaning');
  const [propertyType, setPropertyType] = useState('apartment');
  const [size, setSize] = useState('');
  const [rooms, setRooms] = useState('');
  const [address, setAddress] = useState('');
  const [preferredTime, setPreferredTime] = useState('');
  const [estimate, setEstimate] = useState(null);

  const calculateEstimate = async () => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/jobs/estimate`, {
        service_type: serviceType,
        property_type: propertyType,
        size: parseFloat(size),
        rooms: parseInt(rooms),
        add_ons: []
      });
      setEstimate(res.data);
    } catch (error) {
      alert('Failed to calculate estimate');
    }
  };

  const createJob = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/jobs`, {
        service_type: serviceType,
        property_type: propertyType,
        size: parseFloat(size),
        rooms: parseInt(rooms),
        add_ons: [],
        photos: [],
        preferred_time: preferredTime,
        address: address,
        location: { lat: 40.7128, lng: -74.0060 }
      });
      alert('Job created successfully!');
      onBack();
    } catch (error) {
      alert('Failed to create job');
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 p-4">
      <div className="max-w-2xl mx-auto">
        <button onClick={onBack} className="mb-4 text-primary hover:underline">← Back to Dashboard</button>
        
        <div className="bg-white p-8 rounded-2xl shadow">
          <h2 className="text-2xl font-bold text-primary mb-6">Create New Job</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Service Type</label>
              <select
                value={serviceType}
                onChange={(e) => setServiceType(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-stone-300 focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="regular_cleaning">Regular Cleaning</option>
                <option value="deep_cleaning">Deep Cleaning</option>
                <option value="move_out_cleaning">Move-Out Cleaning</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Property Type</label>
              <select
                value={propertyType}
                onChange={(e) => setPropertyType(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-stone-300 focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="apartment">Apartment</option>
                <option value="villa">Villa</option>
                <option value="office">Office</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Size (sq ft)</label>
                <input
                  type="number"
                  value={size}
                  onChange={(e) => setSize(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-stone-300 focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Rooms</label>
                <input
                  type="number"
                  value={rooms}
                  onChange={(e) => setRooms(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-stone-300 focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Address</label>
              <input
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-stone-300 focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Preferred Time</label>
              <input
                type="datetime-local"
                value={preferredTime}
                onChange={(e) => setPreferredTime(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-stone-300 focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            {estimate && (
              <div className="bg-green-50 p-4 rounded-xl">
                <h3 className="font-semibold mb-2">Price Estimate</h3>
                <div className="text-3xl font-bold text-primary">${estimate.total}</div>
                <div className="text-sm text-stone-600 mt-2">
                  Base: ${estimate.base_price} + Add-ons: ${estimate.add_on_price}
                </div>
              </div>
            )}

            <div className="flex gap-4">
              <button
                onClick={calculateEstimate}
                disabled={!size || !rooms}
                className="flex-1 px-6 py-3 bg-stone-200 rounded-full font-semibold hover:bg-stone-300 disabled:opacity-50"
              >
                Get Estimate
              </button>
              <button
                onClick={createJob}
                disabled={!estimate || !address || !preferredTime}
                className="flex-1 px-6 py-3 bg-primary text-white rounded-full font-semibold hover:bg-opacity-90 disabled:opacity-50"
              >
                Create Job
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Provider Dashboard
const ProviderDashboard = ({ user, onLogout }) => {
  const [jobs, setJobs] = useState([]);
  const [isAvailable, setIsAvailable] = useState(false);

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/jobs`);
      setJobs(res.data);
    } catch (error) {
      console.error('Failed to fetch jobs');
    }
  };

  const toggleAvailability = async () => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/providers/availability`);
      setIsAvailable(res.data.is_available);
      alert(res.data.is_available ? 'Now available for jobs' : 'Now unavailable');
    } catch (error) {
      alert('Failed to update availability');
    }
  };

  const acceptJob = async (jobId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/jobs/${jobId}/accept`);
      alert('Job accepted!');
      fetchJobs();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to accept job');
    }
  };

  if (user.id_verification_status !== 'approved') {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="bg-white p-12 rounded-2xl shadow text-center">
          <h2 className="text-2xl font-bold text-primary mb-4">Verification Pending</h2>
          <p className="text-stone-600 mb-6">Your ID verification is under review.</p>
          <button onClick={onLogout} className="px-6 py-2 rounded-full bg-stone-200">Logout</button>
        </div>
      </div>
    );
  }

  const pendingJobs = jobs.filter(j => j.status === 'pending');
  const myJobs = jobs.filter(j => j.assigned_provider === user.id);

  return (
    <div className="min-h-screen bg-stone-50">
      <header className="bg-white border-b border-stone-200 p-4">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-primary">Provider Dashboard</h1>
            <p className="text-stone-600">Welcome, {user.name}</p>
          </div>
          <div className="flex gap-4">
            <button
              onClick={toggleAvailability}
              className={`px-6 py-2 rounded-full font-semibold ${
                isAvailable ? 'bg-green-600 text-white' : 'bg-stone-200'
              }`}
            >
              {isAvailable ? 'Available' : 'Unavailable'}
            </button>
            <button onClick={onLogout} className="px-6 py-2 rounded-full bg-stone-200">Logout</button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-4">
        <section className="mb-8">
          <h2 className="text-xl font-bold mb-4">Available Jobs Near You</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {pendingJobs.map(job => (
              <div key={job.id} className="bg-white p-6 rounded-2xl shadow">
                <div className="flex justify-between items-start mb-4">
                  <span className="px-3 py-1 rounded-full text-sm bg-yellow-100 text-yellow-800">New Job</span>
                  <span className="text-2xl font-bold text-primary">${job.pricing?.total}</span>
                </div>
                <h3 className="font-semibold text-lg capitalize mb-2">{job.service_type?.replace('_', ' ')}</h3>
                <p className="text-stone-600 text-sm mb-4">{job.address}</p>
                <button
                  onClick={() => acceptJob(job.id)}
                  className="w-full px-6 py-2 bg-primary text-white rounded-full font-semibold hover:bg-opacity-90"
                >
                  Accept Job
                </button>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2 className="text-xl font-bold mb-4">Your Accepted Jobs</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {myJobs.map(job => (
              <div key={job.id} className="bg-white p-6 rounded-2xl shadow">
                <span className="px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800">In Progress</span>
                <h3 className="font-semibold text-lg capitalize mt-2 mb-2">{job.service_type?.replace('_', ' ')}</h3>
                <p className="text-stone-600 text-sm">{job.address}</p>
                <p className="text-2xl font-bold text-primary mt-4">${job.pricing?.total}</p>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
};

// Admin Dashboard
const AdminDashboard = ({ user, onLogout }) => {
  const [verifications, setVerifications] = useState([]);
  const [stats, setStats] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [verRes, statsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/verifications`),
        axios.get(`${BACKEND_URL}/api/admin/stats`)
      ]);
      setVerifications(verRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch data');
    }
  };

  const handleVerification = async (userId, status) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/verifications/${userId}`, {
        status,
        admin_notes: `${status} by admin`
      });
      alert(`Verification ${status}`);
      fetchData();
    } catch (error) {
      alert('Failed to update verification');
    }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <header className="bg-white border-b border-stone-200 p-4">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-primary">Admin Dashboard</h1>
            <p className="text-stone-600">Welcome, {user.name}</p>
          </div>
          <button onClick={onLogout} className="px-6 py-2 rounded-full bg-stone-200">Logout</button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-4">
        <section className="mb-8">
          <h2 className="text-xl font-bold mb-4">Platform Statistics</h2>
          <div className="grid md:grid-cols-4 gap-4">
            <div className="bg-white p-6 rounded-2xl shadow">
              <div className="text-3xl font-bold text-primary">{stats.total_jobs || 0}</div>
              <div className="text-stone-600">Total Jobs</div>
            </div>
            <div className="bg-white p-6 rounded-2xl shadow">
              <div className="text-3xl font-bold text-primary">{stats.active_jobs || 0}</div>
              <div className="text-stone-600">Active Jobs</div>
            </div>
            <div className="bg-white p-6 rounded-2xl shadow">
              <div className="text-3xl font-bold text-primary">${stats.total_revenue || 0}</div>
              <div className="text-stone-600">Total Revenue</div>
            </div>
            <div className="bg-white p-6 rounded-2xl shadow">
              <div className="text-3xl font-bold text-primary">{stats.total_providers || 0}</div>
              <div className="text-stone-600">Providers</div>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-xl font-bold mb-4">Pending Verifications</h2>
          <div className="space-y-4">
            {verifications.map(ver => (
              <div key={ver.id} className="bg-white p-6 rounded-2xl shadow">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg">{ver.name}</h3>
                    <p className="text-stone-600 text-sm">Email: {ver.email}</p>
                    <p className="text-stone-600 text-sm">Phone: {ver.phone}</p>
                    <p className="text-stone-600 text-sm">Role: {ver.role}</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleVerification(ver.id, 'approved')}
                      className="px-6 py-2 bg-green-600 text-white rounded-full font-semibold"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => handleVerification(ver.id, 'rejected')}
                      className="px-6 py-2 bg-red-600 text-white rounded-full font-semibold"
                    >
                      Reject
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
};

// Main App Component
function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/auth/me`);
      setUser(res.data);
    } catch (error) {
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-stone-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <BrowserRouter>
        <Landing onLogin={login} />
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={
          user.role === 'client' ? <ClientDashboard user={user} onLogout={logout} /> :
          user.role === 'provider' ? <ProviderDashboard user={user} onLogout={logout} /> :
          user.role === 'admin' ? <AdminDashboard user={user} onLogout={logout} /> :
          <Navigate to="/" />
        } />
      </Routes>
    </BrowserRouter>
  );
}

export default App;